import { DEFAULT_TARGET_PERCENT, calculateUtilisation, formatDuration, formatHoursCompact } from "../../utils/utilisation";

// Sample data for the static preview. The percentages are not typed in: they are
// calculated from hours and rates with the same formulas the real popup will use,
// so the numbers in the design notes (60% / 86%, 120% / 171%, ...) stay verifiable.

const SAVED_ON = "22/09/2026";

// A fixed-cost project in the popup. `team` is everyone's { minutes, rate } on the
// project; `me` is the logged-in user's entry (it must be one of them).
const fixedCostProject = ({ projectId, projectName, budget, targetPercent = DEFAULT_TARGET_PERCENT, team, meIndex, logCount, loggedMinutes }) => {
  const cost = ({ minutes, rate }) => (minutes / 60) * rate;
  const me = team[meIndex];
  const result = calculateUtilisation({
    budget,
    targetPercent,
    myCost: cost(me),
    projectCost: team.reduce((sum, member) => sum + cost(member), 0),
  });

  return {
    projectId,
    projectName,
    isFixedCost: true,
    logCount,
    durationLabel: formatDuration(loggedMinutes),
    hoursLabel: formatHoursCompact(me.minutes),
    ...result,
  };
};

const hours = (h) => h * 60;

const COMPASSTEX = "Compasstex Mobile App New Template";

const compasstex = (team, extra = {}) =>
  fixedCostProject({
    projectId: 1,
    projectName: COMPASSTEX,
    budget: 100000,
    team,
    meIndex: 1, // Balashankar
    logCount: 1,
    loggedMinutes: 150,
    ...extra,
  });

export const PREVIEW_SCENARIOS = [
  {
    id: "within-budget",
    title: "Within budget (60%)",
    description: "Project at 60% of budget, target 86% used, so the team is still eligible for incentive.",
    summary: { date: SAVED_ON, logCount: 1, durationLabel: "2h 30m", typeLabel: "Regular", billingLabel: "Billable" },
    projects: [
      compasstex([
        { minutes: hours(10), rate: 1000 }, // Balakrishna
        { minutes: hours(50), rate: 600 }, // Balashankar (you)
        { minutes: hours(40), rate: 500 }, // Muthu
      ]),
    ],
  },
  {
    id: "over-budget",
    title: "Budget exceeded (120%)",
    description: "Project at 120% of budget, target 171% used: red bars, warning, no incentive.",
    summary: { date: SAVED_ON, logCount: 1, durationLabel: "3h 00m", typeLabel: "Regular", billingLabel: "Billable" },
    projects: [
      compasstex(
        [
          { minutes: hours(20), rate: 1000 },
          { minutes: hours(100), rate: 600 },
          { minutes: hours(80), rate: 500 },
        ],
        { loggedMinutes: 180 }
      ),
    ],
  },
  {
    id: "target-missed",
    title: "Within budget, target missed (80%)",
    description: "Project at 80% of budget but 114% of the 70% target: still within budget, no incentive.",
    summary: { date: SAVED_ON, logCount: 1, durationLabel: "2h 30m", typeLabel: "Regular", billingLabel: "Billable" },
    projects: [
      compasstex([
        { minutes: hours(30), rate: 1000 },
        { minutes: hours(50), rate: 600 },
        { minutes: hours(40), rate: 500 },
      ]),
    ],
  },
  {
    id: "duplicate-logs",
    title: "Duplicate logs, 2 projects",
    description: "One popup for the whole save, one card per project, each with its own budget and Target %.",
    summary: { date: SAVED_ON, logCount: 3, totalDurationLabel: "7h 30m" },
    projects: [
      compasstex(
        [
          { minutes: hours(10), rate: 1000 },
          { minutes: hours(50), rate: 600 },
          { minutes: hours(40), rate: 500 },
        ],
        { logCount: 2, loggedMinutes: 300 }
      ),
      fixedCostProject({
        projectId: 2,
        projectName: "Client Portal Revamp",
        budget: 300000,
        targetPercent: 80,
        team: [
          { minutes: hours(100), rate: 600 }, // you: 60,000 = 20%
          { minutes: hours(300), rate: 650 }, // others
        ],
        meIndex: 0,
        logCount: 1,
        loggedMinutes: 150,
      }),
    ],
  },
  {
    id: "same-project-batch",
    title: "Duplicate logs, 1 project",
    description: "Several logs for a single project fall back to the normal single popup.",
    summary: { date: SAVED_ON, logCount: 3, totalDurationLabel: "7h 30m" },
    projects: [
      compasstex(
        [
          { minutes: hours(10), rate: 1000 },
          { minutes: hours(50), rate: 600 },
          { minutes: hours(40), rate: 500 },
        ],
        { logCount: 3, loggedMinutes: 450 }
      ),
    ],
  },
  {
    id: "not-fixed-cost",
    title: "Time & Material project",
    description: "No budget to measure against, so only hours are shown.",
    summary: { date: SAVED_ON, logCount: 1, durationLabel: "2h 30m", typeLabel: "Regular", billingLabel: "Billable" },
    projects: [
      {
        projectId: 3,
        projectName: "Support Retainer",
        isFixedCost: false,
        logCount: 1,
        durationLabel: "2h 30m",
        hoursLabel: "32h",
      },
    ],
  },
];
